import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { ShieldCheckIcon } from 'lucide-react'
import { CSSProperties, ReactNode, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, TabbedWidgetWrapper, WidgetInputToggleField } from '../../rapid-ui'
import { CanvasData } from '../canvas/data'
import {
	showCanvasToolbarAtom,
	storyAdAiImageryDetectedAtom,
	storyAdCanvasLoadCounterAtom,
	storyAdMetaCanvasDocumentAtom,
	storyAdMetaCanvasEnabledAtom,
	storyAdMetaForceAiGeneratedLabelAtom,
} from '../state'
import { AiGeneratedLabelToggle } from './ai-generated-label-toggle'
import { CanvasExportDialog, CanvasImportDialog } from './canvas-document-dialogs'
import { DesignFilmstrip } from './design-filmstrip'
import { StoryAdImageWidget } from './story-ad-image-widget'
import { StoryAdLayerManager } from './story-ad-layer-manager'

export type StoryAdWidgetChildrenHelpers = {
	widget: ReactNode
}

export interface StoryAdWidgetProps {
	children: (helpers: StoryAdWidgetChildrenHelpers) => ReactNode
	globalTools: ReactNode
}

export function StoryAdWidget(
	{ children, globalTools }: StoryAdWidgetProps,
) {
	const { t } = useTranslation('common')
	const [safeZoneOpacity, setSafeZoneOpacity] = useState(0)
	const [canvasEnabled, setCanvasEnabled] = useAtom(storyAdMetaCanvasEnabledAtom)
	const canvasDocument = useAtomValue(storyAdMetaCanvasDocumentAtom)
	const setCanvasDocument = useSetAtom(storyAdMetaCanvasDocumentAtom)
	const setLoadCounter = useSetAtom(storyAdCanvasLoadCounterAtom)
	const [forceAiGeneratedLabel, setForceAiGeneratedLabel] = useAtom(storyAdMetaForceAiGeneratedLabelAtom)
	const aiImageryDetected = useAtomValue(storyAdAiImageryDetectedAtom)

	const showCanvasTools = useAtomValue(showCanvasToolbarAtom)

	const [exportOpen, setExportOpen] = useState(false)
	const [importOpen, setImportOpen] = useState(false)

	const handleImport = useCallback((doc: CanvasData) => {
		setCanvasDocument(JSON.stringify(doc))
		setLoadCounter((c) => c + 1)
	}, [setCanvasDocument, setLoadCounter])

	const tabs = [
		{
			id: 'image',
			label: t('editor.widgets.image'),
			children: <StoryAdImageWidget />,
		},
		...(!canvasEnabled
			? [{
				id: 'layers',
				label: t('editor.widgets.layers'),
				children: <StoryAdLayerManager />,
			}]
			: []),
		...(canvasEnabled
			? [{
				id: 'designs',
				label: 'Designs',
				children: (
					<DesignFilmstrip
						onApplyDesign={(doc) => {
							setCanvasDocument(doc)
							setLoadCounter((c) => c + 1)
						}}
					/>
				),
			}]
			: []),
		{
			id: 'tools',
			label: t('editor.widgets.tools'),
			children: (
				<>
					{globalTools}
					<div className="flex flex-col divide-y divide-grey-100 mt-3 text-left">
						<AiGeneratedLabelToggle
							forced={forceAiGeneratedLabel ?? false}
							onForcedChange={setForceAiGeneratedLabel}
							detected={aiImageryDetected}
						/>
						<label className="flex items-center gap-2 py-2">
							<WidgetInputToggleField
								value={safeZoneOpacity > 0}
								onChange={(value) => setSafeZoneOpacity(value ? 1 : 0)}
							/>
							{t('editor.widgets.showSafeZone')}
						</label>
						{showCanvasTools && (
							<div className="py-2">
								<label className="flex items-center gap-2">
									<WidgetInputToggleField
										value={canvasEnabled ?? false}
										onChange={(value) => setCanvasEnabled(value)}
									/>
									<ShieldCheckIcon className="size-3 text-grey-400" />
									{t('editor.widgets.canvasMode')} (experimental early preview)
								</label>
								{canvasEnabled && (
									<div className="flex items-center gap-2 mt-1 pl-12">
										<Button type="subtle" size="tiny" onClick={() => setExportOpen(true)}>
											Export JSON
										</Button>
										<Button type="subtle" size="tiny" onClick={() => setImportOpen(true)}>
											Import JSON
										</Button>
									</div>
								)}
							</div>
						)}
					</div>
				</>
			),
		},
	]

	const [currentTab, setCurrentTab] = useState<typeof tabs[number]['id']>('image')
	const effectiveTab = currentTab === 'layers' && canvasEnabled ? 'image' : currentTab

	const widget = (
		<>
			<TabbedWidgetWrapper
				value={effectiveTab}
				onChange={(v) => setCurrentTab(v)}
				tabs={tabs}
				name={t('editor.widgets.storyAd')}
				animateHeight={false}
			>
			</TabbedWidgetWrapper>
		</>
	)

	const styles = {
		'--safe-zone-opacity': safeZoneOpacity,
	} as CSSProperties

	return (
		<div style={styles}>
			{children({ widget })}
			<CanvasExportDialog open={exportOpen} onClose={() => setExportOpen(false)} document={canvasDocument} />
			<CanvasImportDialog open={importOpen} onClose={() => setImportOpen(false)} onLoad={handleImport} />
		</div>
	)
}
